import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Structures from "@/pages/Structures";
import StructureDetail from "@/pages/StructureDetail";
import Handover from "@/pages/Handover";
import Inspections from "@/pages/Inspections";
import Admin from "@/pages/Admin";
import ActivityLog from "@/pages/ActivityLog";
import Documents from "@/pages/Documents";
import RAMS from "@/pages/RAMS";
import RAMSDetail from "@/pages/RAMSDetail";
import MCCain from "@/pages/MCCain";
import Visits from "@/pages/Visits";
import Timesheets from "@/pages/Timesheets";
import Callouts from "@/pages/Callouts";
import Subcontractors from "@/pages/Subcontractors";
import People from "@/pages/People";
import Activate from "@/pages/Activate";
import NRSAck from "@/pages/NRSAck";
import Verify from "@/pages/Verify";
import Rota from "@/pages/Rota";
import DailyLog from "@/pages/DailyLog";
import WeeklyReport from "@/pages/WeeklyReport";
import McCainFolder from "@/pages/McCainFolder";
import SiteMap from "@/pages/SiteMap";
import AdminBackup from "@/pages/AdminBackup";
import Reds from "@/pages/Reds";
import UnderDevelopment from "@/pages/UnderDevelopment";

const MCCAIN_ROLES = ["mccain", "mccain_external", "mccain_supervisor", "mccain_management"];

function Protected({ children, allow }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div data-testid="auth-loading" style={{ padding: 48, color: "#64748b", fontFamily: "IBM Plex Mono, monospace" }}>
        Authenticating…
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  // McCain auth gate: ensure McCain roles only access pages they're explicitly granted via
  // the per-route `allow` lists. The legacy sandbox-redirect has been removed (Phase 1a) —
  // McCain users now navigate via the standard AppShell sidebar like any other tier.
  const isMcCain = ["mccain", "mccain_external", "mccain_supervisor", "mccain_management"].includes(user.role);
  if (allow && !allow.includes(user.role)) {
    // McCain users without access to a Newell-only page bounce to their dashboard, not to `/`,
    // since `/` itself is now Newell-tiers-only and would cause an infinite redirect.
    return <Navigate to={isMcCain ? "/mccain" : "/"} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrandingProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/nrs/ack" element={<NRSAck />} />
            <Route path="/verify/:kind/:ref" element={<Verify />} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative", "subcontractor"]}>
                  <Dashboard />
                </Protected>
              } />
              <Route
                path="/mccain"
                element={
                  <Protected allow={[...MCCAIN_ROLES, "admin", "contracts_manager"]}>
                    <MCCain />
                  </Protected>
                }
              />
              <Route path="/structures" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative"]}>
                  <Structures />
                </Protected>
              } />
              <Route path="/structures/:id" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative"]}>
                  <StructureDetail />
                </Protected>
              } />
              <Route
                path="/handover"
                element={
                  <Protected allow={["admin", "contracts_manager", "supervisor"]}>
                    <Handover />
                  </Protected>
                }
              />
              <Route path="/inspections" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative"]}>
                  <Inspections />
                </Protected>
              } />
              <Route
                path="/admin"
                element={
                  <Protected allow={["admin"]}>
                    <Admin />
                  </Protected>
                }
              />
              <Route
                path="/activity-log"
                element={
                  <Protected allow={["admin"]}>
                    <ActivityLog />
                  </Protected>
                }
              />
              <Route path="/documents" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative", "subcontractor", "mccain_supervisor", "mccain_management"]}>
                  <Documents />
                </Protected>
              } />
              <Route path="/rams" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative"]}>
                  <RAMS />
                </Protected>
              } />
              <Route path="/rams/:id" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative"]}>
                  <RAMSDetail />
                </Protected>
              } />
              <Route path="/callouts" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person"]}>
                  <Callouts />
                </Protected>
              } />
              <Route path="/rota" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person"]}>
                  <Rota />
                </Protected>
              } />
              <Route path="/daily-logs" element={
                <Protected allow={["admin", "contracts_manager", "supervisor"]}>
                  <DailyLog />
                </Protected>
              } />
              <Route path="/weekly-report" element={
                <Protected allow={["admin", "contracts_manager", "supervisor"]}>
                  <WeeklyReport />
                </Protected>
              } />
              <Route path="/mccain-folder" element={
                <Protected allow={["admin", "contracts_manager"]}>
                  <McCainFolder />
                </Protected>
              } />
              <Route path="/site-map" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "mccain_supervisor", "mccain_management"]}>
                  <SiteMap />
                </Protected>
              } />
              <Route path="/reds" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "mccain_supervisor", "mccain_management", "mccain"]}>
                  <Reds />
                </Protected>
              } />
              <Route path="/admin/backup" element={
                <Protected allow={["admin"]}>
                  <AdminBackup />
                </Protected>
              } />
              <Route path="/visits" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative", "subcontractor"]}>
                  <Visits />
                </Protected>
              } />
              <Route path="/timesheets" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "operative", "subcontractor"]}>
                  <Timesheets />
                </Protected>
              } />
              <Route path="/admin/subcontractors" element={
                <Protected allow={["admin", "contracts_manager"]}>
                  <Subcontractors />
                </Protected>
              } />
              <Route path="/people" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "competent_person", "operative"]}>
                  <People />
                </Protected>
              } />
              <Route path="/admin/users" element={<Protected allow={["admin"]}><UnderDevelopment title="User Management" eta="Phase 1f" description="Create / disable Newell user accounts, set roles (Admin / Contracts Manager / Supervisor / Competent Person / McCain external read-only), reset passwords. Auth + RBAC are already enforced — this screen exposes them." /></Protected>} />
              <Route path="/admin/timesheets" element={
                <Protected allow={["admin", "contracts_manager", "supervisor", "operative"]}>
                  <Timesheets />
                </Protected>
              } />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </BrandingProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
